// Profile Settings Page
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Loader2, User, Lock, Shield, CheckCircle, XCircle, ArrowLeft } from 'lucide-react';

export function ProfileSettings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Profile state
  const [profile, setProfile] = useState({
    full_name: '',
    email: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    country: '',
    phone: ''
  });
  
  // Password state
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  
  // 2FA state
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);
  const [twoFASecret, setTwoFASecret] = useState('');
  const [twoFAQR, setTwoFAQR] = useState('');
  const [twoFACode, setTwoFACode] = useState('');
  const [disablePassword, setDisablePassword] = useState('');
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('profile');
  
  // Subscription management state
  const [showCancelFlow, setShowCancelFlow] = useState(false);
  const [cancelStep, setCancelStep] = useState(1); // 1: reason, 2: retention offer, 3: confirm
  const [cancelReason, setCancelReason] = useState('');
  const [cancelFeedback, setCancelFeedback] = useState('');
  const [retentionChoice, setRetentionChoice] = useState('');

  useEffect(() => {
    fetchProfile();
    fetch2FAStatus();
  }, []);

  const fetchProfile = async () => {
    try {
      const data = await api.request('/profile/me');
      setProfile({
        full_name: data.full_name || '',
        email: data.email || '',
        address: data.address || '',
        city: data.city || '',
        state: data.state || '',
        zip_code: data.zip_code || '',
        country: data.country || '',
        phone: data.phone || ''
      });
    } catch (err) {
      setError('Failed to load profile');
    } finally {
      setLoadingProfile(false);
    }
  };

  const fetch2FAStatus = async () => {
    try {
      const data = await api.request('/profile/2fa/status');
      setTwoFAEnabled(data.enabled);
    } catch (err) {
      console.error('Failed to load 2FA status:', err);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await api.request('/profile/update', {
        method: 'PUT',
        body: JSON.stringify({
          full_name: profile.full_name,
          address: profile.address,
          city: profile.city,
          state: profile.state,
          zip_code: profile.zip_code,
          country: profile.country,
          phone: profile.phone
        })
      });
      setSuccess('Profile updated successfully!');
    } catch (err) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (passwordData.new_password !== passwordData.confirm_password) {
      setError('New passwords do not match');
      setLoading(false);
      return;
    }

    if (passwordData.new_password.length < 8) {
      setError('Password must be at least 8 characters');
      setLoading(false);
      return;
    }

    try {
      await api.request('/profile/change-password', {
        method: 'POST',
        body: JSON.stringify({
          current_password: passwordData.current_password,
          new_password: passwordData.new_password
        })
      });
      setSuccess('Password changed successfully!');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      setError(err.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleEnable2FA = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const data = await api.request('/profile/2fa/enable', { method: 'POST' });
      setTwoFASecret(data.secret);
      setTwoFAQR(data.qr_code_url);
      setSuccess('Scan the QR code with your authenticator app and enter the code below');
    } catch (err) {
      setError(err.message || 'Failed to enable 2FA');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await api.request('/profile/2fa/verify', {
        method: 'POST',
        body: JSON.stringify({ code: twoFACode })
      });
      setSuccess('2FA enabled successfully!');
      setTwoFAEnabled(true);
      setTwoFASecret('');
      setTwoFAQR('');
      setTwoFACode('');
    } catch (err) {
      setError(err.message || 'Invalid 2FA code');
    } finally {
      setLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await api.request('/profile/2fa/disable', {
        method: 'POST',
        body: JSON.stringify({ password: disablePassword })
      });
      setSuccess('2FA disabled successfully');
      setTwoFAEnabled(false);
      setDisablePassword('');
    } catch (err) {
      setError(err.message || 'Failed to disable 2FA');
    } finally {
      setLoading(false);
    }
  };

  if (loadingProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin h-8 w-8" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="container mx-auto max-w-4xl">
        <div className="mb-6">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-4">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Dashboard
          </Button>
          <h1 className="text-3xl font-bold">Profile Settings</h1>
          <p className="text-gray-600 mt-2">Manage your account settings and preferences</p>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-6">
            <XCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert className="mb-6 bg-green-50 border-green-200">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800">{success}</AlertDescription>
          </Alert>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4 mb-6">
            <TabsTrigger value="profile">
              <User className="mr-2 h-4 w-4" />
              Profile
            </TabsTrigger>
            <TabsTrigger value="password">
              <Lock className="mr-2 h-4 w-4" />
              Password
            </TabsTrigger>
            <TabsTrigger value="security">
              <Shield className="mr-2 h-4 w-4" />
              Security
            </TabsTrigger>
            <TabsTrigger value="subscription">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="mr-2">
                <rect x="2" y="5" width="20" height="14" rx="2"/>
                <line x1="2" y1="10" x2="22" y2="10"/>
              </svg>
              Subscription
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card>
              <CardHeader>
                <CardTitle>Personal Information</CardTitle>
                <CardDescription>Update your personal details and contact information</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleUpdateProfile} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Full Name *</label>
                      <Input
                        value={profile.full_name}
                        onChange={(e) => setProfile({ ...profile, full_name: e.target.value })}
                        placeholder="John Doe"
                        required
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Email</label>
                      <Input
                        value={profile.email}
                        disabled
                        className="bg-gray-100"
                      />
                      <p className="text-xs text-gray-500">Email cannot be changed</p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Address</label>
                    <Input
                      value={profile.address}
                      onChange={(e) => setProfile({ ...profile, address: e.target.value })}
                      placeholder="123 Main Street"
                    />
                  </div>

                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">City</label>
                      <Input
                        value={profile.city}
                        onChange={(e) => setProfile({ ...profile, city: e.target.value })}
                        placeholder="New York"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <label className="text-sm font-medium">State</label>
                      <Input
                        value={profile.state}
                        onChange={(e) => setProfile({ ...profile, state: e.target.value })}
                        placeholder="NY"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <label className="text-sm font-medium">ZIP Code</label>
                      <Input
                        value={profile.zip_code}
                        onChange={(e) => setProfile({ ...profile, zip_code: e.target.value })}
                        placeholder="10001"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Country</label>
                      <Input
                        value={profile.country}
                        onChange={(e) => setProfile({ ...profile, country: e.target.value })}
                        placeholder="United States"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Phone</label>
                      <Input
                        value={profile.phone}
                        onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                  </div>

                  <Button type="submit" disabled={loading}>
                    {loading ? (
                      <>
                        <Loader2 className="animate-spin mr-2" size={16} />
                        Saving...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Password Tab */}
          <TabsContent value="password">
            <Card>
              <CardHeader>
                <CardTitle>Change Password</CardTitle>
                <CardDescription>Update your password to keep your account secure</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleChangePassword} className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Current Password *</label>
                    <Input
                      type="password"
                      value={passwordData.current_password}
                      onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                      placeholder="Enter current password"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">New Password *</label>
                    <Input
                      type="password"
                      value={passwordData.new_password}
                      onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                      placeholder="Enter new password (min 8 characters)"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Confirm New Password *</label>
                    <Input
                      type="password"
                      value={passwordData.confirm_password}
                      onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                      placeholder="Confirm new password"
                      required
                    />
                  </div>

                  <Button type="submit" disabled={loading}>
                    {loading ? (
                      <>
                        <Loader2 className="animate-spin mr-2" size={16} />
                        Changing...
                      </>
                    ) : (
                      'Change Password'
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security">
            <Card>
              <CardHeader>
                <CardTitle>Two-Factor Authentication (2FA)</CardTitle>
                <CardDescription>Add an extra layer of security to your account</CardDescription>
              </CardHeader>
              <CardContent>
                {!twoFAEnabled && !twoFAQR && (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600">
                      Two-factor authentication adds an extra layer of security to your account by requiring a code from your phone in addition to your password.
                    </p>
                    <Button onClick={handleEnable2FA} disabled={loading}>
                      {loading ? (
                        <>
                          <Loader2 className="animate-spin mr-2" size={16} />
                          Setting up...
                        </>
                      ) : (
                        'Enable 2FA'
                      )}
                    </Button>
                  </div>
                )}

                {twoFAQR && !twoFAEnabled && (
                  <div className="space-y-4">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm font-medium mb-2">Step 1: Scan QR Code</p>
                      <p className="text-sm text-gray-600 mb-4">
                        Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
                      </p>
                      <img src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(twoFAQR)}`} alt="2FA QR Code" className="mx-auto" />
                      <p className="text-xs text-gray-500 mt-4 text-center">Or enter this code manually: {twoFASecret}</p>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium">Step 2: Enter Verification Code</label>
                      <Input
                        value={twoFACode}
                        onChange={(e) => setTwoFACode(e.target.value)}
                        placeholder="Enter 6-digit code"
                        maxLength={6}
                      />
                    </div>

                    <Button onClick={handleVerify2FA} disabled={loading || twoFACode.length !== 6}>
                      {loading ? (
                        <>
                          <Loader2 className="animate-spin mr-2" size={16} />
                          Verifying...
                        </>
                      ) : (
                        'Verify & Enable 2FA'
                      )}
                    </Button>
                  </div>
                )}

                {twoFAEnabled && (
                  <div className="space-y-4">
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-sm font-medium text-green-800 flex items-center gap-2">
                        <CheckCircle className="h-4 w-4" />
                        Two-Factor Authentication is Enabled
                      </p>
                      <p className="text-sm text-green-700 mt-1">
                        Your account is protected with 2FA
                      </p>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium">Enter Password to Disable 2FA</label>
                      <Input
                        type="password"
                        value={disablePassword}
                        onChange={(e) => setDisablePassword(e.target.value)}
                        placeholder="Enter your password"
                      />
                    </div>

                    <Button
                      variant="destructive"
                      onClick={handleDisable2FA}
                      disabled={loading || !disablePassword}
                    >
                      {loading ? (
                        <>
                          <Loader2 className="animate-spin mr-2" size={16} />
                          Disabling...
                        </>
                      ) : (
                        'Disable 2FA'
                      )}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>


          {/* Subscription Tab */}
          <TabsContent value="subscription">
            <Card>
              <CardHeader>
                <CardTitle>Subscription Management</CardTitle>
                <CardDescription>Manage your subscription plan and billing</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Current Plan */}
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-6 border border-purple-200">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Current Plan</h3>
                      <p className="text-sm text-gray-600">Your active subscription tier</p>
                    </div>
                    <span className={`px-4 py-2 rounded-full font-bold ${
                      user?.subscription_tier === 'premium' ? 'bg-purple-600 text-white' :
                      user?.subscription_tier === 'pro' ? 'bg-purple-100 text-purple-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {user?.subscription_tier?.toUpperCase() || 'FREE'}
                    </span>
                  </div>
                  
                  {user?.subscription_tier && user.subscription_tier !== 'free' ? (
                    <>
                      <div className="grid sm:grid-cols-2 gap-4 mb-4">
                        <div>
                          <div className="text-sm text-gray-600">Monthly Price</div>
                          <div className="text-2xl font-bold text-gray-900">
                            ${user.subscription_tier === 'premium' ? '69.99' : '29.99'}
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-600">Billing Cycle</div>
                          <div className="text-lg font-semibold text-gray-900">Monthly</div>
                        </div>
                      </div>

                      <div className="space-y-2 mb-4">
                        <div className="flex items-center gap-2 text-sm">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          <span>{user.subscription_tier === 'premium' ? '5' : '3'} AI Revisions per request</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          <span>{user.subscription_tier === 'premium' ? 'Unlimited' : '150,000'} AI words/month</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          <span>Advanced screenplay conversions</span>
                        </div>
                      </div>

                      <div className="flex gap-3">
                        <Button variant="outline" onClick={() => navigate('/pricing')}>
                          {user.subscription_tier === 'pro' ? 'Upgrade to Premium' : 'Change Plan'}
                        </Button>
                        <Button 
                          variant="outline" 
                          className="text-red-600 border-red-300 hover:bg-red-50"
                          onClick={() => setShowCancelFlow(true)}
                        >
                          Cancel Subscription
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-gray-600 mb-4">You're currently on the free plan.</p>
                      <Button onClick={() => navigate('/pricing')} className="bg-purple-600 hover:bg-purple-700">
                        Upgrade to Pro or Premium
                      </Button>
                    </div>
                  )}
                </div>

                {/* Cancellation Flow Modal */}
                {showCancelFlow && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                      {/* Step 1: Reason for Cancellation */}
                      {cancelStep === 1 && (
                        <div className="p-6">
                          <h3 className="text-2xl font-bold mb-2">We're sorry to see you go 😢</h3>
                          <p className="text-gray-600 mb-6">Help us improve by sharing why you're canceling:</p>
                          
                          <div className="space-y-3 mb-6">
                            {[
                              { value: 'too_expensive', label: '💰 Too expensive' },
                              { value: 'not_using', label: '⏸️ Not using it enough' },
                              { value: 'missing_features', label: '🔧 Missing features I need' },
                              { value: 'technical_issues', label: '🐛 Technical issues or bugs' },
                              { value: 'switching_service', label: '🔄 Switching to another service' },
                              { value: 'temporary', label: '⏰ Taking a break, plan to return' },
                              { value: 'other', label: '📝 Other reason' }
                            ].map((reason) => (
                              <button
                                key={reason.value}
                                onClick={() => setCancelReason(reason.value)}
                                className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                                  cancelReason === reason.value
                                    ? 'border-purple-600 bg-purple-50'
                                    : 'border-gray-200 hover:border-purple-300'
                                }`}
                              >
                                {reason.label}
                              </button>
                            ))}
                          </div>

                          <div className="mb-6">
                            <label className="block text-sm font-medium mb-2">
                              Additional feedback (optional):
                            </label>
                            <textarea
                              value={cancelFeedback}
                              onChange={(e) => setCancelFeedback(e.target.value)}
                              placeholder="Tell us more about your experience..."
                              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                              rows={4}
                            />
                          </div>

                          <div className="flex gap-3">
                            <Button
                              variant="outline"
                              onClick={() => {
                                setShowCancelFlow(false);
                                setCancelStep(1);
                                setCancelReason('');
                                setCancelFeedback('');
                              }}
                              className="flex-1"
                            >
                              Nevermind, Keep My Plan
                            </Button>
                            <Button
                              onClick={() => setCancelStep(2)}
                              disabled={!cancelReason}
                              className="flex-1 bg-purple-600 hover:bg-purple-700"
                            >
                              Continue →
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* Step 2: Retention Offers */}
                      {cancelStep === 2 && (
                        <div className="p-6">
                          <h3 className="text-2xl font-bold mb-2">Wait! We have an offer for you 🎁</h3>
                          <p className="text-gray-600 mb-6">
                            We value you as a customer. Choose one of these exclusive offers:
                          </p>

                          <div className="space-y-4 mb-6">
                            {/* Offer 1: Free Credits */}
                            <button
                              onClick={() => setRetentionChoice('free_credits')}
                              className={`w-full text-left p-6 rounded-lg border-2 transition-all ${
                                retentionChoice === 'free_credits'
                                  ? 'border-purple-600 bg-purple-50'
                                  : 'border-gray-200 hover:border-purple-300'
                              }`}
                            >
                              <div className="flex items-start gap-4">
                                <div className="text-4xl">✨</div>
                                <div className="flex-1">
                                  <h4 className="text-lg font-bold text-purple-900 mb-1">
                                    Get 50 FREE Craft Credits
                                  </h4>
                                  <p className="text-sm text-gray-600 mb-2">
                                    Worth $9.99! Keep your subscription and get 50 craft editing credits instantly.
                                  </p>
                                  <div className="text-sm font-semibold text-purple-600">
                                    Stay subscribed + Get 50 credits FREE
                                  </div>
                                </div>
                              </div>
                            </button>

                            {/* Offer 2: Pause Subscription */}
                            <button
                              onClick={() => setRetentionChoice('pause')}
                              className={`w-full text-left p-6 rounded-lg border-2 transition-all ${
                                retentionChoice === 'pause'
                                  ? 'border-blue-600 bg-blue-50'
                                  : 'border-gray-200 hover:border-blue-300'
                              }`}
                            >
                              <div className="flex items-start gap-4">
                                <div className="text-4xl">⏸️</div>
                                <div className="flex-1">
                                  <h4 className="text-lg font-bold text-blue-900 mb-1">
                                    Pause for 2 Months
                                  </h4>
                                  <p className="text-sm text-gray-600 mb-2">
                                    Need a break? Pause billing for 2 months. Your data stays safe, resume anytime.
                                  </p>
                                  <div className="text-sm font-semibold text-blue-600">
                                    No charges for 2 months • Resume anytime
                                  </div>
                                </div>
                              </div>
                            </button>

                            {/* Offer 3: 25% Discount */}
                            <button
                              onClick={() => setRetentionChoice('discount')}
                              className={`w-full text-left p-6 rounded-lg border-2 transition-all ${
                                retentionChoice === 'discount'
                                  ? 'border-green-600 bg-green-50'
                                  : 'border-gray-200 hover:border-green-300'
                              }`}
                            >
                              <div className="flex items-start gap-4">
                                <div className="text-4xl">💰</div>
                                <div className="flex-1">
                                  <h4 className="text-lg font-bold text-green-900 mb-1">
                                    25% Off for 3 Months
                                  </h4>
                                  <p className="text-sm text-gray-600 mb-2">
                                    Save ${user?.subscription_tier === 'premium' ? '52.49' : '22.49'} total! Get 25% off your next 3 monthly bills.
                                  </p>
                                  <div className="text-sm font-semibold text-green-600">
                                    ${user?.subscription_tier === 'premium' ? '52.49' : '22.49'}/month for 3 months
                                  </div>
                                </div>
                              </div>
                            </button>

                            {/* Option 4: Still Cancel */}
                            <button
                              onClick={() => setRetentionChoice('cancel')}
                              className={`w-full text-left p-6 rounded-lg border-2 transition-all ${
                                retentionChoice === 'cancel'
                                  ? 'border-red-600 bg-red-50'
                                  : 'border-gray-200 hover:border-red-300'
                              }`}
                            >
                              <div className="flex items-start gap-4">
                                <div className="text-4xl">😢</div>
                                <div className="flex-1">
                                  <h4 className="text-lg font-bold text-red-900 mb-1">
                                    Proceed with Cancellation
                                  </h4>
                                  <p className="text-sm text-gray-600">
                                    Cancel immediately. You'll lose access at the end of your billing period.
                                  </p>
                                </div>
                              </div>
                            </button>
                          </div>

                          <div className="flex gap-3">
                            <Button
                              variant="outline"
                              onClick={() => setCancelStep(1)}
                              className="flex-1"
                            >
                              ← Back
                            </Button>
                            <Button
                              onClick={() => setCancelStep(3)}
                              disabled={!retentionChoice}
                              className="flex-1 bg-purple-600 hover:bg-purple-700"
                            >
                              Continue →
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* Step 3: Confirmation */}
                      {cancelStep === 3 && (
                        <div className="p-6">
                          <h3 className="text-2xl font-bold mb-4">
                            {retentionChoice === 'cancel' ? 'Confirm Cancellation' : 'Confirm Your Choice'}
                          </h3>
                          
                          {retentionChoice === 'free_credits' && (
                            <div className="bg-purple-50 border border-purple-200 rounded-lg p-6 mb-6">
                              <div className="text-4xl mb-3">🎉</div>
                              <h4 className="text-lg font-bold text-purple-900 mb-2">You're getting 50 FREE Craft Credits!</h4>
                              <p className="text-gray-700">
                                Your subscription will continue, and 50 craft editing credits ($9.99 value) will be added to your account immediately.
                              </p>
                            </div>
                          )}

                          {retentionChoice === 'pause' && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                              <div className="text-4xl mb-3">⏸️</div>
                              <h4 className="text-lg font-bold text-blue-900 mb-2">Your subscription will be paused</h4>
                              <p className="text-gray-700">
                                You won't be charged for the next 2 months. All your data stays safe. Resume anytime from your profile settings.
                              </p>
                            </div>
                          )}

                          {retentionChoice === 'discount' && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
                              <div className="text-4xl mb-3">💰</div>
                              <h4 className="text-lg font-bold text-green-900 mb-2">You're getting 25% off!</h4>
                              <p className="text-gray-700">
                                Your next 3 monthly bills will be discounted by 25%. That's ${user?.subscription_tier === 'premium' ? '17.50' : '7.50'} off each month!
                              </p>
                            </div>
                          )}

                          {retentionChoice === 'cancel' && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
                              <div className="text-4xl mb-3">😢</div>
                              <h4 className="text-lg font-bold text-red-900 mb-2">Your subscription will be canceled</h4>
                              <p className="text-gray-700 mb-3">
                                You'll lose access to Pro/Premium features at the end of your current billing period. Your documents will remain accessible.
                              </p>
                              <div className="bg-white rounded p-3 text-sm">
                                <strong>What you'll lose:</strong>
                                <ul className="list-disc list-inside mt-2 text-gray-600">
                                  <li>Multi-revision AI (3-5 versions per request)</li>
                                  <li>Unlimited/Extended AI words</li>
                                  <li>Advanced screenplay conversions</li>
                                </ul>
                              </div>
                            </div>
                          )}

                          <div className="flex gap-3">
                            <Button
                              variant="outline"
                              onClick={() => setCancelStep(2)}
                              className="flex-1"
                            >
                              ← Back
                            </Button>
                            <Button
                              onClick={async () => {
                                try {
                                  setLoading(true);
                                  await api.request('/subscription/retention', {
                                    method: 'POST',
                                    body: JSON.stringify({
                                      action: retentionChoice,
                                      cancel_reason: cancelReason,
                                      cancel_feedback: cancelFeedback
                                    })
                                  });
                                  
                                  setShowCancelFlow(false);
                                  setCancelStep(1);
                                  
                                  if (retentionChoice === 'cancel') {
                                    setSuccess('Your subscription has been canceled. You\'ll retain access until the end of your billing period.');
                                  } else if (retentionChoice === 'free_credits') {
                                    setSuccess('🎉 50 craft credits added to your account! Thank you for staying with us.');
                                  } else if (retentionChoice === 'pause') {
                                    setSuccess('Your subscription has been paused for 2 months. Resume anytime from settings.');
                                  } else if (retentionChoice === 'discount') {
                                    setSuccess('25% discount applied! Your next 3 bills will reflect the savings.');
                                  }
                                  
                                  // Reload profile to get updated data
                                  fetchProfile();
                                } catch (err) {
                                  setError(err.message || 'Failed to process your request');
                                } finally {
                                  setLoading(false);
                                }
                              }}
                              disabled={loading}
                              className={`flex-1 ${
                                retentionChoice === 'cancel' 
                                  ? 'bg-red-600 hover:bg-red-700' 
                                  : 'bg-purple-600 hover:bg-purple-700'
                              }`}
                            >
                              {loading ? (
                                <>
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  Processing...
                                </>
                              ) : (
                                'Confirm'
                              )}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

        </Tabs>
      </div>
    </div>
  );
}
